import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { API_BASE_URL, authFetch } from "../../config/api";
import { formatMoney } from "../../utils/formatMoney";

const MANILA_TIME_ZONE = "Asia/Manila";
const CHECK_CUTOFF_HOUR = 16;

const getManilaParts = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return null;

  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: MANILA_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const parts = Object.fromEntries(
    formatter
      .formatToParts(date)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value])
  );

  return {
    hour: Number(parts.hour),
    minute: Number(parts.minute),
  };
};

const isAtOrAfterCutoff = (parts) =>
  Boolean(parts) && (parts.hour > CHECK_CUTOFF_HOUR || (parts.hour === CHECK_CUTOFF_HOUR && parts.minute >= 0));

const paymentModes = [
  "Petty Cash",
  "Cash",
  "Check",
  "Manager's Check",
  "Telegraphic Transfer",
  "Online Banking Transfer",
];

const TAX_TYPE_OPTIONS = [
  { value: "", label: "No Tax", rate: 0 },
  { value: "tax_12", label: "12%", rate: 12 },
  { value: "specific_amount", label: "Specific Amount", rate: 0 },
];

const EWT_OPTIONS = [
  { value: "", label: "No EWT", rate: 0 },
  { value: "ewt_1", label: "EWT 1%", rate: 1 },
  { value: "ewt_2", label: "EWT 2%", rate: 2 },
  { value: "ewt_5", label: "EWT 5%", rate: 5 },
  { value: "ewt_10", label: "EWT 10%", rate: 10 },
  { value: "specific_amount", label: "Specific Amount", rate: 0 },
];

const getEwtBreakdown = (lineTotal, ewtType, ewtAmountInput) => {
  const total = Number(lineTotal || 0);
  if (!ewtType || total <= 0) return 0;

  if (ewtType === "specific_amount") {
    const override = Number(ewtAmountInput || 0);
    return Number(Math.min(Math.max(override, 0), total).toFixed(2));
  }

  const option = EWT_OPTIONS.find((o) => o.value === ewtType);
  const rate = option?.rate || 0;
  if (rate <= 0) return 0;
  return Number(((total * rate) / 100).toFixed(2));
};

const getTaxBreakdown = (grossAmount, taxType, taxAmountInput) => {
  const gross = Number(grossAmount || 0);
  if (!taxType || gross <= 0) {
    return { netAmount: gross, taxAmount: 0 };
  }

  if (taxType === "specific_amount") {
    const taxAmount = Number(Math.min(Math.max(Number(taxAmountInput || 0), 0), gross).toFixed(2));
    return { netAmount: Number((gross - taxAmount).toFixed(2)), taxAmount };
  }

  const option = TAX_TYPE_OPTIONS.find((o) => o.value === taxType);
  const rate = option?.rate || 0;
  if (rate <= 0) return { netAmount: gross, taxAmount: 0 };

  const netAmount = gross / (1 + rate / 100);
  const taxAmount = gross - netAmount;

  return {
    netAmount: Number(netAmount.toFixed(2)),
    taxAmount: Number(taxAmount.toFixed(2)),
  };
};

const RequestCreate = () => {
  const createAttachmentRow = () => ({ id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, file: null });
  const [modeOfPayment, setModeOfPayment] = useState("");
  const [type, setType] = useState("operational");
  const [shipmentModal, setShipmentModal] = useState(false);
  const [shipments, setShipments] = useState([]);
  const [shipmentSearch, setShipmentSearch] = useState("");
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [shipPage, setShipPage] = useState(1);
  const [shipTotal, setShipTotal] = useState(0);
  const shipLimit = 20;

  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [payeeType, setPayeeType] = useState("vendor");
  const [payeeId, setPayeeId] = useState("");
  const [payeeName, setPayeeName] = useState("");
  const [payeeCurrency, setPayeeCurrency] = useState("PHP");
  const [exchangeRate, setExchangeRate] = useState("");
  const [particularOptions, setParticularOptions] = useState([]);
  const [payeeModal, setPayeeModal] = useState(false);
  const [payeeSearch, setPayeeSearch] = useState("");
  const [particularModal, setParticularModal] = useState(false);
  const [particularSearch, setParticularSearch] = useState("");
  const [particularRowIndex, setParticularRowIndex] = useState(null);

  const [particulars, setParticulars] = useState([
    { description: "", amount: "", quantity: "1", taxType: "", taxAmount: "", ewtType: "", ewtAmount: "", isFixedCharge: false },
  ]);
  const [forLiquidation, setForLiquidation] = useState(false);
  const [forSales, setForSales] = useState(false);
  const [rush, setRush] = useState(false);
  const [rushReason, setRushReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [requestAttachments, setRequestAttachments] = useState([createAttachmentRow()]);
  const [status, setStatus] = useState("draft");
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const payeeSelectionLocked = type === "operational" && !selectedShipment;

  // Fetch reference data
  useEffect(() => {
    const loadRefs = async () => {
      const [vRes, cRes, pRes] = await Promise.all([
        authFetch(`${API_BASE_URL}/api/accounting/vendors`, { credentials: "include" }),
        authFetch(`${API_BASE_URL}/api/accounting/customers`, { credentials: "include" }),
        authFetch(`${API_BASE_URL}/api/accounting/particulars`, { credentials: "include" }),
      ]);
      const vJson = await vRes.json();
      const cJson = await cRes.json();
      const pJson = await pRes.json();
      if (vJson.success) setVendors(vJson.data || []);
      if (cJson.success) setCustomers(cJson.data || []);
      if (pJson.success) {
        const active = (pJson.data || []).filter((p) => p.status === "active");
        setParticularOptions(active);
      }
    };
    loadRefs();
  }, []);

  // Fetch shipments for picker (using backend minimal endpoint)
  const fetchShipments = async (page = 1) => {
    const url = new URL(`${API_BASE_URL}/api/accounting/shipments`);
    url.searchParams.set("page", page);
    url.searchParams.set("limit", shipLimit);
    if (shipmentSearch) url.searchParams.set("q", shipmentSearch);
    const res = await authFetch(url, { credentials: "include" });
    const data = await res.json();
    if (data.success) {
      setShipments(data.data || []);
      setShipTotal(data.total || 0);
      setShipPage(data.page || 1);
    }
  };

  useEffect(() => {
    if (shipmentModal) fetchShipments(shipPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentModal, shipPage]);

  const payeeOptions = payeeType === "vendor" ? vendors : customers;

  // Auto-set payee from JO (consignee) when operational
  useEffect(() => {
    if (type !== "operational" || !selectedShipment) {
      return;
    }
    const mode = (selectedShipment.shipment_mode || "").toLowerCase();
    const isImport = mode.includes("import");
    const suggestedPayee = isImport
      ? selectedShipment.receiver_name || ""
      : selectedShipment.shipper_name || selectedShipment.receiver_name || "";

    setPayeeName(suggestedPayee);
    setPayeeType("customer");
    const match = customers.find(
      (c) => (c.customer || "").toLowerCase() === suggestedPayee.toLowerCase()
    );
    setPayeeId(match ? match.id : "");
  }, [type, selectedShipment, customers]);

  useEffect(() => {
    const selectedPayee = payeeOptions.find((p) => p.id === payeeId);
    const currency = (selectedPayee?.currency || "PHP").toUpperCase();
    setPayeeCurrency(currency);
    if (currency === "USD") {
      setExchangeRate((current) => current || "56.00");
    } else {
      setExchangeRate("");
    }
  }, [payeeId, payeeOptions]);

  const totalAmount = useMemo(
    () =>
      particulars.reduce(
        (sum, p) => sum + Number(p.amount || 0) * Number(p.quantity || 1),
        0
      ),
    [particulars]
  );

  const totalTaxAmount = useMemo(
    () =>
      particulars.reduce((sum, p) => {
        const lineTotal = Number(p.amount || 0) * Number(p.quantity || 1);
        return sum + getTaxBreakdown(lineTotal, p.taxType, p.taxAmount).taxAmount;
      }, 0),
    [particulars]
  );

  const totalNetOfTaxAmount = useMemo(
    () =>
      particulars.reduce((sum, p) => {
        const lineTotal = Number(p.amount || 0) * Number(p.quantity || 1);
        return sum + getTaxBreakdown(lineTotal, p.taxType, p.taxAmount).netAmount;
      }, 0),
    [particulars]
  );

  const totalEwtAmount = useMemo(
    () =>
      particulars.reduce((sum, p) => {
        const lineTotal = Number(p.amount || 0) * Number(p.quantity || 1);
        const { netAmount } = getTaxBreakdown(lineTotal, p.taxType, p.taxAmount);
        return sum + getEwtBreakdown(netAmount, p.ewtType, p.ewtAmount);
      }, 0),
    [particulars]
  );

  const netPayableAmount = Number((totalAmount - totalEwtAmount).toFixed(2));

  const conversionRate = Number(exchangeRate || 1) || 1;
  const convertedTotal =
    payeeCurrency === "USD" ? totalAmount * conversionRate : totalAmount;
  const convertedNetPayable =
    payeeCurrency === "USD" ? netPayableAmount * conversionRate : netPayableAmount;
  const hasRequestAttachments = requestAttachments.some((row) => row.file);
  const filteredPayees = useMemo(() => {
    return payeeOptions.filter((p) => {
      if (!payeeSearch) return true;
      const term = payeeSearch.toLowerCase();
      if (payeeType === "customer") {
        return (
          (p.customer || "").toLowerCase().includes(term) ||
          (p.company || "").toLowerCase().includes(term) ||
          (p.contact_person || "").toLowerCase().includes(term)
        );
      }
      return (
        (p.vendor || "").toLowerCase().includes(term) ||
        (p.account_no || "").toLowerCase().includes(term) ||
        (p.main_phone || "").toLowerCase().includes(term)
      );
    });
  }, [payeeOptions, payeeSearch, payeeType]);
  const filteredParticularOptions = useMemo(() => {
    const term = particularSearch.trim().toLowerCase();
    if (!term) return particularOptions;
    return particularOptions.filter((opt) => {
      const name = String(opt.name || "").toLowerCase();
      const description = String(opt.description || "").toLowerCase();
      const code = String(opt.code || "").toLowerCase();
      return name.includes(term) || description.includes(term) || code.includes(term);
    });
  }, [particularOptions, particularSearch]);

  const updateParticular = (idx, field, value) => {
    setParticulars((rows) =>
      rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r))
    );
  };

  const openParticularPicker = (idx) => {
    setParticularRowIndex(idx);
    setParticularSearch("");
    setParticularModal(true);
  };

  const selectParticular = (particular) => {
    if (particularRowIndex === null || particularRowIndex === undefined) return;
    updateParticular(particularRowIndex, "description", particular.name || "");
    setParticularModal(false);
    setParticularSearch("");
    setParticularRowIndex(null);
  };

  const addRow = () =>
    setParticulars((rows) => [
      ...rows,
      { description: "", amount: "", quantity: "1", taxRate: 0, ewtType: "", ewtAmount: "", isFixedCharge: false },
    ]);

  const removeRow = (idx) =>
    setParticulars((rows) => rows.filter((_, i) => i !== idx));

  const selectRequestType = (nextType) => {
    setType(nextType);
    if (nextType !== "non_operational") {
      setForSales(false);
    }
  };

  const handleSubmit = async () => {
    if (type === "operational" && !selectedShipment) {
      setErrorMsg("Please pick a Job Order for operational requests.");
      return;
    }
    if (!particulars.length || !particulars[0].description) {
      setErrorMsg("Add at least one particular.");
      return;
    }
    if (type === "non_operational" && !payeeId) {
      setErrorMsg("Please select a payee.");
      return;
    }
    if (payeeCurrency === "USD" && (!exchangeRate || Number(exchangeRate) <= 0)) {
      setErrorMsg("USD requests must include a valid USD to PHP conversion rate.");
      return;
    }
    if (rush && !rushReason.trim()) {
      setErrorMsg("Rush requests must include a reason.");
      return;
    }

    setSaving(true);
    setErrorMsg("");
    try {
      const payload = {
        type,
        shipment_id: type === "operational" ? selectedShipment?.shipment_id : null,
        job_order: type === "operational" ? selectedShipment?.job_order : null,
        payee: payeeName || "",
        payee_id: payeeId || null,
        payee_type: payeeType,
        mode_of_payment: modeOfPayment,
        currency: payeeCurrency,
        exchange_rate: payeeCurrency === "USD" ? conversionRate : 1,
        for_liquidation: forLiquidation,
        for_sales: type === "non_operational" && forSales,
        rush,
        rush_reason: rush ? rushReason.trim() : null,
        status,
        due_date: dueDate || null,
        remarks,
        particulars: particulars.map((p) => {
          const baseTotal = Number(p.amount || 0) * Number(p.quantity || 1);
          const taxType = p.taxType || null;
          const taxOption = TAX_TYPE_OPTIONS.find((o) => o.value === taxType);
          const { netAmount, taxAmount } = getTaxBreakdown(baseTotal, taxType, p.taxAmount);
          const ewtType = p.ewtType || null;
          const ewtOption = EWT_OPTIONS.find((o) => o.value === ewtType);
          const ewtAmount = getEwtBreakdown(netAmount, ewtType, p.ewtAmount);
          return {
            description: p.description,
            amount: Number(p.amount || 0),
            quantity: Number(p.quantity || 1),
            with_tax: Boolean(taxType),
            tax_type: taxType,
            tax_rate: taxType && taxType !== "specific_amount" ? taxOption?.rate || null : null,
            tax_amount: taxAmount,
            net_amount: netAmount,
            ewt_type: ewtType,
            ewt_rate: ewtType && ewtType !== "specific_amount" ? ewtOption?.rate || null : null,
            ewt_amount: ewtAmount,
            is_fixed_charge: Boolean(p.isFixedCharge),
          };
        }),
      };

      const useMultipart = hasRequestAttachments;
      const requestBody = useMultipart
        ? (() => {
            const formData = new FormData();
            Object.entries(payload).forEach(([key, value]) => {
              if (key === "particulars") {
                formData.append(key, JSON.stringify(value));
              } else if (value !== undefined && value !== null) {
                formData.append(key, typeof value === "boolean" ? String(value) : String(value));
              }
            });
            requestAttachments.forEach((row) => {
              if (row.file) {
                formData.append("files", row.file);
              }
            });
            return formData;
          })()
        : JSON.stringify(payload);

      const res = await authFetch(`${API_BASE_URL}/api/accounting/requests`, {
        method: "POST",
        ...(useMultipart ? {} : { headers: { "Content-Type": "application/json" } }),
        credentials: "include",
        body: requestBody,
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Save failed");
      const submittedAfterCutoff = status === "pending" && isAtOrAfterCutoff(getManilaParts(new Date()));
      setSuccessMessage(
        submittedAfterCutoff && !rush
          ? "Your request was saved and will be processed tomorrow because it was submitted after the 4:00 PM cutoff."
          : rush
            ? "Your rush request was saved successfully. It will be handled today with the rush reason provided."
            : "Your request has been saved successfully."
      );
      setShowSuccess(true);
      resetRequestForm();
    } catch (err) {
      console.error(err);
      setErrorMsg(err.message);
    } finally {
      setSaving(false);
    }
  };

  const addRequestAttachment = () => {
    setRequestAttachments((current) => [...current, createAttachmentRow()]);
  };

  const updateRequestAttachment = (id, file) => {
    setRequestAttachments((current) =>
      current.map((row) => (row.id === id ? { ...row, file } : row))
    );
  };

  const removeRequestAttachment = (id) => {
    setRequestAttachments((current) => {
      const next = current.filter((row) => row.id !== id);
      return next.length ? next : [createAttachmentRow()];
    });
  };

  const resetRequestForm = () => {
    setModeOfPayment("");
    setType("operational");
    setShipmentModal(false);
    setShipments([]);
    setShipmentSearch("");
    setSelectedShipment(null);
    setShipPage(1);
    setShipTotal(0);
    setPayeeType("vendor");
    setPayeeId("");
    setPayeeName("");
    setPayeeCurrency("PHP");
    setExchangeRate("");
    setParticulars([
      { description: "", amount: "", quantity: "1", taxRate: 0, ewtType: "", ewtAmount: "", isFixedCharge: false },
    ]);
    setForLiquidation(false);
    setForSales(false);
    setRush(false);
    setRushReason("");
    setRemarks("");
    setDueDate("");
    setRequestAttachments([createAttachmentRow()]);
    setStatus("draft");
    setErrorMsg("");
    setPayeeSearch("");
    setPayeeModal(false);
  };

  return (
    <div className="space-y-6 font-poppins">
      <div className="bg-white/95 border border-gray-100 rounded-3xl shadow-sm p-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.14em] text-gray-500">Accounting</p>
          <h1 className="text-2xl font-semibold text-blue">Request Voucher</h1>
          <p className="text-sm text-gray-600">Create operational or non-operational requests.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-sm font-semibold text-gray-700">Due Date:</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
          </div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
          >
            <option value="draft">Save as Draft</option>
            <option value="pending">Submit (Pending)</option>
          </select>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-blue text-white text-sm font-semibold hover:bg-blue/90 shadow-sm disabled:opacity-60"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <div className="bg-white/95 border border-gray-100 rounded-3xl shadow-sm p-5 space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-700">Type:</span>
            <div className="flex gap-2">
              <button
                className={`px-3 py-1.5 rounded-lg text-sm border ${
                  type === "operational"
                    ? "bg-blue text-white border-blue"
                    : "border-gray-200 text-gray-700"
                }`}
                onClick={() => selectRequestType("operational")}
              >
                Operational (JO)
              </button>
              <button
                className={`px-3 py-1.5 rounded-lg text-sm border ${
                  type === "non_operational"
                    ? "bg-blue text-white border-blue"
                    : "border-gray-200 text-gray-700"
                }`}
                onClick={() => selectRequestType("non_operational")}
              >
                Non-Operational
              </button>
            </div>
          </div>

          {type === "non_operational" && (
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={forSales}
                onChange={(e) => setForSales(e.target.checked)}
                className="w-4 h-4"
              />
              For Sales
            </label>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-700">Mode of Payment:</span>
            <select
              value={modeOfPayment}
              onChange={(e) => setModeOfPayment(e.target.value)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
            >
              <option value="">Select</option>
              {paymentModes.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-700">Currency:</span>
            <select
              value={payeeCurrency}
              onChange={(e) => {
                const nextCurrency = e.target.value.toUpperCase();
                setPayeeCurrency(nextCurrency);
                if (nextCurrency === "USD") {
                  setExchangeRate((current) => current || "56.00");
                } else {
                  setExchangeRate("");
                }
              }}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
            >
              <option value="PHP">PHP</option>
              <option value="USD">USD</option>
            </select>
          </div>

          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={forLiquidation}
              onChange={(e) => setForLiquidation(e.target.checked)}
              className="w-4 h-4"
            />
            For Liquidation
          </label>

          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={rush}
              onChange={(e) => setRush(e.target.checked)}
              className="w-4 h-4"
            />
            Rush request
          </label>
        </div>

        {rush && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3">
            <label className="text-sm font-semibold text-amber-900">Rush reason</label>
            <textarea
              value={rushReason}
              onChange={(e) => setRushReason(e.target.value)}
              className="mt-2 w-full rounded-xl border border-amber-200 px-3 py-2 text-sm bg-white"
              placeholder="Explain why this request must bypass the 4:00 PM cutoff"
            />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            {type === "operational" && (
              <div className="bg-gray-50 border border-gray-100 rounded-2xl p-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-700">Job Order</p>
                  <button
                    type="button"
                    onClick={() => setShipmentModal(true)}
                    className="text-blue text-sm font-semibold"
                  >
                    Pick JO
                  </button>
                </div>
                {selectedShipment ? (
                  <div className="mt-2 text-sm text-gray-700 space-y-1">
                    <div className="font-semibold">
                      {selectedShipment.job_order || "No JO"} — {selectedShipment.shipment_description}
                    </div>
                    <div>Client: {selectedShipment.corporate_client}</div>
                    <div>Consignee: {selectedShipment.receiver_name}</div>
                    <div>Shipper: {selectedShipment.shipper_name}</div>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 mt-2">No job order selected.</p>
                )}
              </div>
            )}

            <div className={`bg-gray-50 border border-gray-100 rounded-2xl p-3 ${payeeSelectionLocked ? "opacity-75" : ""}`}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm font-semibold text-gray-700">Payee</p>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-wrap w-full sm:w-auto">
                  <select
                    value={payeeType}
                    disabled={payeeSelectionLocked}
                    onChange={(e) => {
                      setPayeeType(e.target.value);
                      setPayeeId("");
                    }}
                    className="w-full sm:w-auto rounded-lg border border-gray-200 px-2 py-2 text-sm disabled:cursor-not-allowed disabled:bg-gray-100"
                  >
                    <option value="vendor">Vendor</option>
                    <option value="customer">Customer</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      if (payeeSelectionLocked) {
                        setShipmentModal(true);
                        return;
                      }
                      setPayeeSearch("");
                      setPayeeModal(true);
                    }}
                    disabled={payeeSelectionLocked}
                    className="text-blue text-sm font-semibold disabled:cursor-not-allowed disabled:text-gray-400 w-full sm:w-auto text-left sm:text-right"
                  >
                    Pick {payeeType === "customer" ? "Customer" : "Vendor"}
                  </button>
                </div>
              </div>
              <div className="mt-2">
                <input
                  type="text"
                  value={payeeName}
                  disabled={payeeSelectionLocked}
                  onChange={(e) => {
                    setPayeeName(e.target.value);
                    setPayeeId("");
                  }}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm disabled:cursor-not-allowed disabled:bg-gray-100"
                  placeholder={payeeSelectionLocked ? "Pick Job Order first" : "Enter payee name"}
                />
              </div>
              {payeeSelectionLocked ? (
                <div className="mt-2 text-xs text-amber-700">
                  Pick a Job Order first to enable payee selection.
                </div>
              ) : payeeId ? (
                <div className="mt-2 text-xs text-gray-500">
                  Linked to {payeeType === "customer" ? "Customer" : "Vendor"} • Currency: {payeeCurrency}
                </div>
              ) : null}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-semibold text-gray-700">Remarks</label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              className="rounded-2xl border border-gray-200 px-3 py-2 text-sm min-h-[100px]"
              placeholder="Optional remarks"
            />
            <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-700">Attachments</p>
                  <p className="text-xs text-gray-500">
                    Add one or more supporting files for the request. These will be saved with the request.
                  </p>
                </div>
                {hasRequestAttachments ? (
                  <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue">
                    {requestAttachments.filter((row) => row.file).length} file(s)
                  </span>
                ) : null}
              </div>
              <div className="mt-3 space-y-2">
                {requestAttachments.map((row, index) => (
                  <div
                    key={row.id}
                    className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm"
                  >
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx"
                      onChange={(e) => updateRequestAttachment(row.id, e.target.files?.[0] || null)}
                      className="block w-full text-sm"
                    />
                    {row.file ? (
                      <span className="min-w-0 flex-1 truncate text-xs text-gray-500">
                        {row.file.name}
                      </span>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => removeRequestAttachment(row.id)}
                      className="text-xs font-semibold text-red hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={addRequestAttachment}
                  className="inline-flex items-center gap-2 rounded-xl border border-blue/20 bg-blue/5 px-3 py-2 text-sm font-semibold text-blue hover:bg-blue/10"
                >
                  <i className="fa-solid fa-plus" />
                  Add attachment
                </button>
                {hasRequestAttachments && (
                  <div className="text-xs text-gray-500">
                    {requestAttachments.filter((row) => row.file).length} ready to upload
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-100 rounded-2xl p-3">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <h3 className="text-sm font-semibold text-gray-700">Particulars</h3>
            <button
              onClick={addRow}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-white"
            >
              + Add Row
            </button>
          </div>
          <div className="overflow-x-auto hidden md:block">
            <table className="min-w-full text-sm">
              <thead className="bg-white text-gray-600">
                <tr>
                  <th className="px-3 py-2 text-left">Description</th>
                  <th className="px-3 py-2 text-left">
                    Amount {payeeCurrency === "USD" ? "(USD)" : ""}
                  </th>
                  <th className="px-3 py-2 text-left">Qty</th>
                  <th className="px-3 py-2 text-left">Input Tax</th>
                  <th className="px-3 py-2 text-left">EWT</th>
                  <th className="px-3 py-2 text-center">Fixed Charge</th>
                  <th className="px-3 py-2 text-left">Total</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {particulars.map((row, idx) => {
                  const total = Number(row.amount || 0) * Number(row.quantity || 1);
                  return (
                    <tr key={idx}>
                      <td className="px-3 py-2">
                        <div className="flex gap-2">
                          <input
                            value={row.description}
                            onChange={(e) => updateParticular(idx, "description", e.target.value)}
                            className="w-full rounded-lg border border-gray-200 px-2 py-2 text-sm bg-white"
                            placeholder="Type or pick particular"
                          />
                          <button
                            type="button"
                            onClick={() => openParticularPicker(idx)}
                            className="shrink-0 rounded-lg border border-blue/30 px-3 py-2 text-xs font-semibold text-blue hover:bg-blue/10"
                          >
                            Pick
                          </button>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={row.amount}
                          onChange={(e) => updateParticular(idx, "amount", e.target.value)}
                          className="w-24 rounded-lg border border-gray-200 px-2 py-1"
                          placeholder={payeeCurrency === "USD" ? "USD amount" : "Amount"}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={row.quantity}
                          onChange={(e) => updateParticular(idx, "quantity", e.target.value)}
                          className="w-16 rounded-lg border border-gray-200 px-2 py-1"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          <select
                            value={row.taxType || ""}
                            onChange={(e) => updateParticular(idx, "taxType", e.target.value)}
                            className="rounded-lg border border-gray-200 px-2 py-1 text-sm"
                          >
                            {TAX_TYPE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          {row.taxType === "specific_amount" && (
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={row.taxAmount}
                              onChange={(e) => updateParticular(idx, "taxAmount", e.target.value)}
                              className="w-24 rounded-lg border border-gray-200 px-2 py-1 text-sm"
                              placeholder="Amount"
                            />
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          <select
                            value={row.ewtType || ""}
                            onChange={(e) => updateParticular(idx, "ewtType", e.target.value)}
                            className="rounded-lg border border-gray-200 px-2 py-1 text-sm"
                          >
                            {EWT_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          {row.ewtType === "specific_amount" && (
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={row.ewtAmount}
                              onChange={(e) => updateParticular(idx, "ewtAmount", e.target.value)}
                              className="w-24 rounded-lg border border-gray-200 px-2 py-1 text-sm"
                              placeholder="Amount"
                            />
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={Boolean(row.isFixedCharge)}
                          onChange={(e) => updateParticular(idx, "isFixedCharge", e.target.checked)}
                          className="w-4 h-4"
                          title="Fixed charge"
                        />
                      </td>
                      <td className="px-3 py-2 text-gray-800 font-semibold">{formatMoney(total)}</td>
                      <td className="px-3 py-2 text-right">
                        {particulars.length > 1 && (
                          <button
                            onClick={() => removeRow(idx)}
                            className="text-red text-xs font-semibold hover:underline"
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="space-y-3 md:hidden">
            {particulars.map((row, idx) => {
              const total = Number(row.amount || 0) * Number(row.quantity || 1);
              return (
                <div key={idx} className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <label className="text-xs font-semibold text-gray-500">Description</label>
                      <div className="mt-1 flex gap-2">
                        <input
                          value={row.description}
                          onChange={(e) => updateParticular(idx, "description", e.target.value)}
                          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800"
                          placeholder="Type or pick particular"
                        />
                        <button
                          type="button"
                          onClick={() => openParticularPicker(idx)}
                          className="shrink-0 rounded-lg border border-blue/30 px-3 py-2 text-xs font-semibold text-blue hover:bg-blue/10"
                        >
                          Pick
                        </button>
                      </div>
                    </div>
                    {particulars.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeRow(idx)}
                        className="shrink-0 pt-5 text-xs font-semibold text-red hover:underline"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-500">Amount</label>
                      <input
                        type="number"
                        value={row.amount}
                        onChange={(e) => updateParticular(idx, "amount", e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        placeholder={payeeCurrency === "USD" ? "USD amount" : "Amount"}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500">Qty</label>
                      <input
                        type="number"
                        value={row.quantity}
                        onChange={(e) => updateParticular(idx, "quantity", e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500">Input Tax</label>
                      <select
                        value={row.taxType || ""}
                        onChange={(e) => updateParticular(idx, "taxType", e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                      >
                        {TAX_TYPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {row.taxType === "specific_amount" && (
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={row.taxAmount}
                          onChange={(e) => updateParticular(idx, "taxAmount", e.target.value)}
                          className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                          placeholder="Tax amount"
                        />
                      )}
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500">EWT</label>
                      <select
                        value={row.ewtType || ""}
                        onChange={(e) => updateParticular(idx, "ewtType", e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                      >
                        {EWT_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {row.ewtType === "specific_amount" && (
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={row.ewtAmount}
                          onChange={(e) => updateParticular(idx, "ewtAmount", e.target.value)}
                          className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                          placeholder="EWT amount"
                        />
                      )}
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-between">
                    <label className="inline-flex items-center gap-2 text-xs font-semibold text-gray-600">
                      <input
                        type="checkbox"
                        checked={Boolean(row.isFixedCharge)}
                        onChange={(e) => updateParticular(idx, "isFixedCharge", e.target.checked)}
                        className="w-4 h-4"
                      />
                      Fixed charge
                    </label>
                    <div className="text-right text-sm text-gray-800">
                      Total: <span className="font-semibold">{formatMoney(total)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {payeeCurrency === "USD" && (
            <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50/70 p-3">
              <label className="text-xs font-semibold text-gray-700">Conversion Rate to PHP</label>
              <input
                type="number"
                min="0"
                step="0.0001"
                value={exchangeRate}
                onChange={(e) => setExchangeRate(e.target.value)}
                className="mt-1 w-full max-w-xs rounded-lg border border-amber-200 px-3 py-2 text-sm bg-white"
                placeholder="Enter USD to PHP rate"
              />
              <p className="mt-1 text-xs text-gray-500">
                Used to convert the USD total into PHP before saving.
              </p>
            </div>
          )}

          <div className="mt-4 flex justify-end">
            <div className="w-full sm:w-80 rounded-2xl border border-gray-200 bg-white p-4 text-sm">
              <div className="flex items-center justify-between py-1">
                <span className="text-gray-600">Total (VAT Inclusive)</span>
                <div className="text-right">
                  <div className="font-medium text-gray-800">
                    {payeeCurrency === "USD"
                      ? `$${formatMoney(totalAmount, "en-US")}`
                      : `₱${formatMoney(totalAmount, "en-PH")}`}
                  </div>
                  {payeeCurrency === "USD" && (
                    <div className="text-xs text-gray-500">≈ ₱{formatMoney(convertedTotal, "en-PH")}</div>
                  )}
                </div>
              </div>
              {totalTaxAmount > 0 && (
                <div className="flex items-center justify-between py-1 text-xs text-gray-500">
                  <span>Input Tax (included)</span>
                  <span>
                    {payeeCurrency === "USD"
                      ? `$${formatMoney(totalTaxAmount, "en-US")}`
                      : `₱${formatMoney(totalTaxAmount, "en-PH")}`}
                  </span>
                </div>
              )}
              {totalTaxAmount > 0 && (
                <div className="flex items-center justify-between py-1 text-xs text-gray-500">
                  <span>Net Amount (VAT Exclusive)</span>
                  <span>
                    {payeeCurrency === "USD"
                      ? `$${formatMoney(totalNetOfTaxAmount, "en-US")}`
                      : `₱${formatMoney(totalNetOfTaxAmount, "en-PH")}`}
                  </span>
                </div>
              )}
              {totalEwtAmount > 0 && (
                <div className="flex items-center justify-between py-1">
                  <span className="text-gray-600">Less: EWT</span>
                  <span className="font-medium text-gray-800">
                    ({payeeCurrency === "USD"
                      ? `$${formatMoney(totalEwtAmount, "en-US")}`
                      : `₱${formatMoney(totalEwtAmount, "en-PH")}`})
                  </span>
                </div>
              )}
              <div className="mt-2 flex items-center justify-between border-t border-gray-200 pt-2">
                <span className="font-semibold text-gray-700">Total Amount Due</span>
                <span className="text-base font-bold text-blue">
                  {payeeCurrency === "USD"
                    ? `$${formatMoney(netPayableAmount, "en-US")}`
                    : `₱${formatMoney(netPayableAmount, "en-PH")}`}
                </span>
              </div>
              {payeeCurrency === "USD" && (
                <div className="mt-1 text-right text-xs text-gray-500">
                  ≈ ₱{formatMoney(convertedNetPayable, "en-PH")}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Shipment picker modal */}
      {shipmentModal &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 backdrop-blur-sm px-3 py-4 sm:items-center sm:px-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-[95vw] sm:max-w-4xl p-4 sm:p-6 relative max-h-[92vh] flex flex-col overflow-hidden">
              <button
                onClick={() => setShipmentModal(false)}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-3 pr-8">
                <h3 className="text-lg font-semibold text-blue">Pick Job Order</h3>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2 w-full sm:w-auto">
                  <input
                    value={shipmentSearch}
                    onChange={(e) => setShipmentSearch(e.target.value)}
                    placeholder="Search shipment desc"
                    className="w-full sm:w-72 rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                  <button
                    onClick={() => {
                      setShipPage(1);
                      fetchShipments(1);
                    }}
                    className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 w-full sm:w-auto"
                  >
                    Search
                  </button>
                </div>
              </div>
              <div className="border border-gray-100 rounded-xl overflow-hidden flex-1 min-h-0">
                <div className="px-4 py-2 text-xs text-gray-600 bg-gray-50 border-b border-gray-100">
                  Payee rule: Import/Import-Event → Consignee; Export/Export-Event → Shipper.
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                  <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-gray-700">
                        <tr className="text-left uppercase text-[11px] tracking-wide text-gray-500">
                          <th className="px-4 py-2">JO</th>
                          <th className="px-4 py-2">Description</th>
                          <th className="px-4 py-2">Mode</th>
                          <th className="px-4 py-2">Client</th>
                          <th className="px-4 py-2">Consignee</th>
                          <th className="px-4 py-2">Shipper</th>
                          <th className="px-4 py-2 text-right">Select</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {shipments.map((s) => (
                          <tr key={s.shipment_id} className="hover:bg-gray-50/80">
                            <td className="px-4 py-2 font-semibold">{s.job_order || "—"}</td>
                            <td className="px-4 py-2">{s.shipment_description}</td>
                            <td className="px-4 py-2">{s.shipment_mode || "—"}</td>
                            <td className="px-4 py-2">{s.corporate_client}</td>
                            <td className="px-4 py-2">{s.receiver_name}</td>
                            <td className="px-4 py-2">{s.shipper_name}</td>
                            <td className="px-4 py-2 text-right">
                              <button
                                onClick={() => {
                                  setSelectedShipment(s);
                                  setShipmentModal(false);
                                }}
                                className="px-3 py-1.5 rounded-lg border border-blue/30 text-blue text-xs font-semibold hover:bg-blue/10"
                              >
                                Select
                              </button>
                            </td>
                          </tr>
                        ))}
                        {!shipments.length && (
                          <tr>
                            <td className="px-4 py-4 text-center text-gray-500" colSpan={7}>
                              No shipments found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="space-y-3 md:hidden p-3">
                    {shipments.map((s) => (
                      <div
                        key={s.shipment_id}
                        className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-gray-900">
                              {s.job_order || "—"}
                            </div>
                            <div className="mt-1 text-xs text-gray-500 line-clamp-2">
                              {s.shipment_description || "No description"}
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setSelectedShipment(s);
                              setShipmentModal(false);
                            }}
                            className="shrink-0 px-3 py-1.5 rounded-lg border border-blue/30 text-blue text-xs font-semibold hover:bg-blue/10"
                          >
                            Select
                          </button>
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-600">
                          <div>
                            <span className="font-semibold text-gray-500">Mode:</span>{" "}
                            {s.shipment_mode || "—"}
                          </div>
                          <div>
                            <span className="font-semibold text-gray-500">Client:</span>{" "}
                            {s.corporate_client || "—"}
                          </div>
                          <div>
                            <span className="font-semibold text-gray-500">Consignee:</span>{" "}
                            {s.receiver_name || "—"}
                          </div>
                          <div>
                            <span className="font-semibold text-gray-500">Shipper:</span>{" "}
                            {s.shipper_name || "—"}
                          </div>
                        </div>
                      </div>
                    ))}

                    {!shipments.length && (
                      <div className="py-8 text-center text-gray-500 text-sm">
                        No shipments found.
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-sm text-gray-700">
                <div className="text-center sm:text-left">
                  Page {shipPage} • Showing {shipments.length} of {shipTotal}
                </div>
                <div className="flex gap-2 justify-center sm:justify-end">
                  <button
                    onClick={() => {
                      if (shipPage > 1) fetchShipments(shipPage - 1);
                    }}
                    disabled={shipPage <= 1}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <button
                    onClick={() => {
                      if (shipPage * shipLimit < shipTotal) fetchShipments(shipPage + 1);
                    }}
                    disabled={shipPage * shipLimit >= shipTotal}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      {showSuccess &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
              <button
                onClick={() => setShowSuccess(false)}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
              <h3 className="text-lg font-semibold text-blue mb-2">Request Saved</h3>
              <p className="text-sm text-gray-700 mb-4">
                {successMessage}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowSuccess(false)}
                  className="px-4 py-2 bg-blue text-white rounded-lg text-sm font-semibold hover:bg-blue/90 shadow-sm"
                >
                  OK
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {errorMsg &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
              <button
                onClick={() => setErrorMsg("")}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
              <h3 className="text-lg font-semibold text-red mb-2">Request Error</h3>
              <p className="text-sm text-gray-700 mb-4">{errorMsg}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setErrorMsg("")}
                  className="px-4 py-2 bg-red text-white rounded-lg text-sm font-semibold hover:bg-red/90 shadow-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Particular picker modal */}
      {particularModal &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 backdrop-blur-sm px-3 py-4 sm:items-center sm:px-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-[95vw] sm:max-w-4xl p-4 sm:p-6 relative max-h-[92vh] flex flex-col overflow-hidden">
              <button
                onClick={() => {
                  setParticularModal(false);
                  setParticularSearch("");
                  setParticularRowIndex(null);
                }}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 z-10"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-3 pr-10">
                <h3 className="text-lg font-semibold text-blue">Pick Particular</h3>
                <input
                  value={particularSearch}
                  onChange={(e) => setParticularSearch(e.target.value)}
                  placeholder="Search particular..."
                  className="rounded-lg border border-gray-200 px-3 py-2 text-sm w-full sm:w-72"
                  autoFocus
                />
              </div>
              <div className="border border-gray-100 rounded-xl overflow-hidden flex-1 min-h-0">
                <div className="max-h-[70vh] overflow-y-auto">
                  <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-gray-700 sticky top-0">
                        <tr className="text-left uppercase text-[11px] tracking-wide text-gray-500">
                          <th className="px-4 py-2">Particular</th>
                          <th className="px-4 py-2 text-right">Select</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filteredParticularOptions.map((opt) => (
                          <tr key={opt.id} className="hover:bg-gray-50/80">
                            <td className="px-4 py-2 font-semibold">
                              {opt.name || "—"}
                              {opt.status ? (
                                <span className="ml-2 text-[11px] font-medium text-gray-500">
                                  {opt.status}
                                </span>
                              ) : null}
                            </td>
                            <td className="px-4 py-2 text-right">
                              <button
                                onClick={() => selectParticular(opt)}
                                className="px-3 py-1.5 rounded-lg border border-blue/30 text-blue text-xs font-semibold hover:bg-blue/10"
                              >
                                Select
                              </button>
                            </td>
                          </tr>
                        ))}
                        {filteredParticularOptions.length === 0 && (
                          <tr>
                            <td className="px-4 py-4 text-center text-gray-500" colSpan={2}>
                              No particulars found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="space-y-3 p-3 md:hidden">
                    {filteredParticularOptions.map((opt) => (
                      <div
                        key={opt.id}
                        className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-gray-900">
                              {opt.name || "—"}
                            </div>
                            {opt.status ? (
                              <div className="mt-1 text-xs text-gray-500">{opt.status}</div>
                            ) : null}
                          </div>
                          <button
                            onClick={() => selectParticular(opt)}
                            className="shrink-0 px-3 py-1.5 rounded-lg border border-blue/30 text-blue text-xs font-semibold hover:bg-blue/10"
                          >
                            Select
                          </button>
                        </div>
                      </div>
                    ))}

                    {filteredParticularOptions.length === 0 && (
                      <div className="py-8 text-center text-gray-500 text-sm">
                        No particulars found.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Payee picker modal */}
      {payeeModal &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 backdrop-blur-sm px-3 py-4 sm:items-center sm:px-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-[95vw] sm:max-w-5xl p-4 sm:p-6 relative max-h-[92vh] flex flex-col overflow-hidden">
              <button
                onClick={() => setPayeeModal(false)}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 z-10"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-3 pr-10">
                <h3 className="text-lg font-semibold text-blue">
                  Pick {payeeType === "customer" ? "Customer" : "Vendor"}
                </h3>
                <input
                  value={payeeSearch}
                  onChange={(e) => setPayeeSearch(e.target.value)}
                  placeholder={`Search ${payeeType === "customer" ? "customer" : "vendor"}...`}
                  className="rounded-lg border border-gray-200 px-3 py-2 text-sm w-full sm:w-64"
                  autoFocus
                />
              </div>
              <div className="border border-gray-100 rounded-xl overflow-hidden flex-1 min-h-0">
                <div className="max-h-[70vh] overflow-y-auto">
                  <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-gray-700 sticky top-0">
                        <tr className="text-left uppercase text-[11px] tracking-wide text-gray-500">
                          {payeeType === "customer" ? (
                            <>
                              <th className="px-4 py-2">Customer</th>
                              <th className="px-4 py-2">Company</th>
                              <th className="px-4 py-2">Contact</th>
                              <th className="px-4 py-2">Currency</th>
                            </>
                          ) : (
                            <>
                              <th className="px-4 py-2">Vendor</th>
                              <th className="px-4 py-2">Account No.</th>
                              <th className="px-4 py-2">Main Phone</th>
                              <th className="px-4 py-2">Currency</th>
                            </>
                          )}
                          <th className="px-4 py-2 text-right">Select</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filteredPayees.map((p) => (
                          <tr key={p.id} className="hover:bg-gray-50/80">
                            {payeeType === "customer" ? (
                              <>
                                <td className="px-4 py-2 font-semibold">{p.customer}</td>
                                <td className="px-4 py-2">{p.company || "—"}</td>
                                <td className="px-4 py-2">{p.contact_person || "—"}</td>
                                <td className="px-4 py-2">{(p.currency || "PHP").toUpperCase()}</td>
                              </>
                            ) : (
                              <>
                                <td className="px-4 py-2 font-semibold">{p.vendor}</td>
                                <td className="px-4 py-2">{p.account_no || "—"}</td>
                                <td className="px-4 py-2">{p.main_phone || "—"}</td>
                                <td className="px-4 py-2">{(p.currency || "PHP").toUpperCase()}</td>
                              </>
                            )}
                            <td className="px-4 py-2 text-right">
                              <button
                                onClick={() => {
                                  setPayeeId(p.id);
                                  setPayeeName(payeeType === "customer" ? p.customer : p.vendor);
                                  setPayeeModal(false);
                                }}
                                className="px-3 py-1.5 rounded-lg border border-blue/30 text-blue text-xs font-semibold hover:bg-blue/10"
                              >
                                Select
                              </button>
                            </td>
                          </tr>
                        ))}
                        {filteredPayees.length === 0 && (
                          <tr>
                            <td className="px-4 py-4 text-center text-gray-500" colSpan={5}>
                              No {payeeType === "customer" ? "customers" : "vendors"} found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="space-y-3 p-3 md:hidden">
                    {filteredPayees.map((p) => (
                      <div
                        key={p.id}
                        className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-gray-900">
                              {payeeType === "customer" ? p.customer : p.vendor}
                            </div>
                            <div className="mt-1 text-xs text-gray-500">
                              Currency: {(p.currency || "PHP").toUpperCase()}
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setPayeeId(p.id);
                              setPayeeName(payeeType === "customer" ? p.customer : p.vendor);
                              setPayeeModal(false);
                            }}
                            className="shrink-0 px-3 py-1.5 rounded-lg border border-blue/30 text-blue text-xs font-semibold hover:bg-blue/10"
                          >
                            Select
                          </button>
                        </div>
                        <div className="mt-3 grid grid-cols-1 gap-2 text-xs text-gray-600">
                          {payeeType === "customer" ? (
                            <>
                              <div>
                                <span className="font-semibold text-gray-500">Company:</span>{" "}
                                {p.company || "—"}
                              </div>
                              <div>
                                <span className="font-semibold text-gray-500">Contact:</span>{" "}
                                {p.contact_person || "—"}
                              </div>
                            </>
                          ) : (
                            <>
                              <div>
                                <span className="font-semibold text-gray-500">Account No.:</span>{" "}
                                {p.account_no || "—"}
                              </div>
                              <div>
                                <span className="font-semibold text-gray-500">Main Phone:</span>{" "}
                                {p.main_phone || "—"}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ))}

                    {filteredPayees.length === 0 && (
                      <div className="py-8 text-center text-gray-500 text-sm">
                        No {payeeType === "customer" ? "customers" : "vendors"} found.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default RequestCreate;
